// Shared EDEN delegated-work API client.
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS). Token lookup covers both apps' storage keys.
//
// The backend request row is the authoritative job — this client only reads
// and issues governed transitions (cancel / clarify / mark-read). Nothing
// here can touch platform execution.

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5000").replace(/\/+$/, "");

function readAuthToken() {
  try {
    const storage = window?.localStorage;
    if (!storage) return null;
    return (
      storage.getItem("superadminToken") ||
      storage.getItem("clientAuthToken") ||
      storage.getItem("token") ||
      storage.getItem("authToken") ||
      storage.getItem("accessToken") ||
      null
    );
  } catch {
    return null;
  }
}

async function workRequest(path, { method = "GET", body = null } = {}) {
  const token = readAuthToken();
  if (!token) return { ok: false, status: 401, error: "not_authenticated" };
  try {
    const response = await fetch(`${API_BASE_URL}/api/eden/work${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok && data?.ok !== false, status: response.status, data };
  } catch (error) {
    return { ok: false, status: 0, error: "network_error" };
  }
}

export async function fetchDelegatedWork(id) {
  return workRequest(`/${Number(id)}`);
}

export async function fetchActiveDelegatedWork() {
  return workRequest(`/?active=1`);
}

export async function fetchUnreadWorkNotifications() {
  return workRequest(`/notifications/unread`);
}

export async function markWorkNotificationRead(id) {
  return workRequest(`/${Number(id)}/notification-read`, { method: "POST" });
}

export async function cancelDelegatedWork(id, reason = null) {
  return workRequest(`/${Number(id)}/cancel`, { method: "POST", body: { reason } });
}

export async function clarifyDelegatedWork(id, answers) {
  return workRequest(`/${Number(id)}/clarify`, { method: "POST", body: { answers } });
}

export async function retryDelegatedWork(id) {
  return workRequest(`/${Number(id)}/retry`, { method: "POST" });
}

const edenWorkClient = {
  fetchDelegatedWork,
  fetchActiveDelegatedWork,
  fetchUnreadWorkNotifications,
  markWorkNotificationRead,
  cancelDelegatedWork,
  clarifyDelegatedWork,
  retryDelegatedWork,
};

export default edenWorkClient;
